/**
 * Boli Live - Cart Page
 *
 * Full cart page with item management and checkout summary.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCartStore } from '../lib/stores/cart-store';
import { useAuthStore } from '../lib/stores/auth-store';
import { formatPKR, cn } from '../lib/utils';
import type { CartItem } from '@boli/types';

export default function CartPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const {
    cart,
    isLoading,
    isUpdating,
    fetchCart,
    updateItemQuantity,
    removeItem,
    clearCart,
  } = useCartStore();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="h-8 bg-muted rounded w-40 mb-8 animate-pulse" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {[1, 2, 3].map((i) => (
                <CartItemSkeleton key={i} />
              ))}
            </div>
            <div className="h-64 bg-muted rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Empty cart
  if (isEmpty) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-foreground mb-8">Shopping Cart</h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-12 text-center"
          >
            <ShoppingCart className="w-20 h-20 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-6">
              Looks like you haven't added any items yet.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors"
            >
              <ShoppingBag className="w-5 h-5" />
              Start Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-foreground">
              Shopping Cart ({cart?.itemCount || 0} items)
            </h1>
          </div>
          <button
            onClick={() => clearCart()}
            disabled={isUpdating}
            className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item: CartItem, index: number) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <CartItemRow
                  item={item}
                  isUpdating={isUpdating}
                  onUpdateQuantity={(qty) => updateItemQuantity(item.id, qty)}
                  onRemove={() => removeItem(item.id)}
                />
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-card border border-border rounded-lg p-6 sticky top-24"
            >
              <h2 className="text-lg font-bold text-foreground mb-4">Order Summary</h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal ({cart?.itemCount} items)</span>
                  <span>{formatPKR(cart?.subtotal || 0)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span>{formatPKR(cart?.shippingTotal || 0)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Tax (PST)</span>
                  <span>{formatPKR(cart?.pstTotal || 0)}</span>
                </div>

                <div className="border-t border-border pt-3 mt-3">
                  <div className="flex justify-between text-foreground font-bold text-lg">
                    <span>Total</span>
                    <span>{formatPKR(cart?.grandTotal || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <div className="mt-6 space-y-3">
                {isAuthenticated ? (
                  <Link
                    to="/checkout"
                    className="w-full py-3 bg-primary text-primary-foreground rounded-md text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                  >
                    Proceed to Checkout
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground text-center">
                      Please sign in to checkout
                    </p>
                    <Link
                      to="/profile"
                      className="w-full py-3 bg-primary text-primary-foreground rounded-md text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                    >
                      Sign In to Checkout
                    </Link>
                  </div>
                )}

                <Link
                  to="/"
                  className="w-full py-2 text-primary hover:text-primary/80 text-sm font-medium flex items-center justify-center gap-1 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Continue Shopping
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="mt-6 pt-4 border-t border-border">
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    Secure Checkout
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    Buyer Protection
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    Easy Returns
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    Fast Shipping
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cart Item Row (full-size)
interface CartItemRowProps {
  item: {
    id: string;
    productId: string;
    quantity: number;
    product?: {
      id: string;
      title: string;
      price: number;
      images?: string[];
      stock: number;
      shippingInfo?: {
        originCity?: string;
      };
    };
  };
  isUpdating: boolean;
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
}

function CartItemRow({ item, isUpdating, onUpdateQuantity, onRemove }: CartItemRowProps) {
  const product = item.product;
  const maxQuantity = product?.stock || 10;
  const lineTotal = (product?.price || 0) * item.quantity;

  return (
    <div className="flex gap-4 p-4 bg-card border border-border rounded-lg">
      {/* Image */}
      <Link to={`/product/${item.productId}`} className="flex-shrink-0">
        <div className="w-24 h-24 md:w-32 md:h-32 rounded-md overflow-hidden bg-secondary">
          <img
            src={product?.images?.[0] || '/placeholder-product.jpg'}
            alt={product?.title || 'Product'}
            className="w-full h-full object-cover hover:scale-105 transition-transform"
          />
        </div>
      </Link>

      {/* Details */}
      <div className="flex-1 min-w-0 flex flex-col">
        <Link to={`/product/${item.productId}`}>
          <h3 className="text-sm md:text-base font-medium text-foreground line-clamp-2 hover:underline">
            {product?.title || 'Loading...'}
          </h3>
        </Link>

        {product?.shippingInfo?.originCity && (
          <p className="text-xs text-muted-foreground mt-1">
            Ships from: {product.shippingInfo.originCity}
          </p>
        )}

        <p className="text-sm font-semibold text-foreground mt-1">
          {product ? formatPKR(product.price) : '...'} each
        </p>

        {/* Quantity and Remove - Mobile */}
        <div className="flex items-center justify-between mt-auto pt-3 md:hidden">
          <QuantityControls
            quantity={item.quantity}
            maxQuantity={maxQuantity}
            isUpdating={isUpdating}
            onUpdateQuantity={onUpdateQuantity}
          />
          <button
            onClick={onRemove}
            disabled={isUpdating}
            className="p-2 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quantity - Desktop */}
      <div className="hidden md:flex flex-col items-center justify-center">
        <span className="text-xs text-muted-foreground mb-2">Quantity</span>
        <QuantityControls
          quantity={item.quantity}
          maxQuantity={maxQuantity}
          isUpdating={isUpdating}
          onUpdateQuantity={onUpdateQuantity}
        />
      </div>

      {/* Line Total - Desktop */}
      <div className="hidden md:flex flex-col items-end justify-between min-w-[100px]">
        <span className="text-lg font-bold text-foreground">
          {formatPKR(lineTotal)}
        </span>
        <button
          onClick={onRemove}
          disabled={isUpdating}
          className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          Remove
        </button>
      </div>
    </div>
  );
}

// Quantity Controls
interface QuantityControlsProps {
  quantity: number;
  maxQuantity: number;
  isUpdating: boolean;
  onUpdateQuantity: (quantity: number) => void;
}

function QuantityControls({ quantity, maxQuantity, isUpdating, onUpdateQuantity }: QuantityControlsProps) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onUpdateQuantity(quantity - 1)}
        disabled={isUpdating || quantity <= 1}
        className={cn(
          'w-8 h-8 rounded-md flex items-center justify-center transition-colors',
          'border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <Minus className="w-4 h-4" />
      </button>

      <span className="w-10 text-center text-sm font-medium">{quantity}</span>

      <button
        onClick={() => onUpdateQuantity(quantity + 1)}
        disabled={isUpdating || quantity >= maxQuantity}
        className={cn(
          'w-8 h-8 rounded-md flex items-center justify-center transition-colors',
          'border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

// Skeleton
function CartItemSkeleton() {
  return (
    <div className="flex gap-4 p-4 bg-card border border-border rounded-lg">
      <div className="w-24 h-24 md:w-32 md:h-32 rounded-md bg-muted animate-pulse" />
      <div className="flex-1 space-y-2">
        <div className="h-5 bg-muted rounded w-3/4 animate-pulse" />
        <div className="h-4 bg-muted rounded w-1/4 animate-pulse" />
        <div className="h-4 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-8 bg-muted rounded w-24 animate-pulse mt-4" />
      </div>
      <div className="hidden md:block w-24">
        <div className="h-6 bg-muted rounded animate-pulse" />
      </div>
    </div>
  );
}
